import React, { useState, useContext } from "react";
import { Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import InputText from "../../input/inputText";
import DateInput from "../../input/date";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { DashIcon, InfoIcon } from "./../svg";
import {
  setScheduleContentText,
  setScheduleIsShowNothing,
  setIsSchedule,
} from "../../../redux/slices/settings";
import ScheduleDays from "./ScheduleDays";
import FileUpload from "../../input/fileUpload";
import { ContextAll } from "../../saveFile/saveFile";
import PopoverBtn from "../../popoverBtn";
import InputReactSelect from "../../input/inputReactSelect";

const ScheduleAccess = ({
  stateKey,
  setScheduleTimezone,
  setScheduleDateStart,
  setScheduleDateEnd,
  addDayItem,
  applyAll,
  removeDayItem,
  resetDayField,
  setDayField,
  setToggleActiveDay,
  scheduleTitle,
  error,
}) => {
  const { locale, lang, dateLang, bookingLang, optional, errorMsg } =
    useSelector((state) => ({
      locale: state.changeLang.change,
      lang: state.changeLang.language.restriction,
      dateLang: state.changeLang.language.stepTwo.vcard,
      bookingLang: state.changeLang.language.stepTwo.booking,
      optional: state.changeLang.language.stepTwo.optional[0],
      errorMsg: state.changeLang.language.stepTwo.errors,
    }));

  const scheduleState = useSelector((state) =>
    stateKey ? state[stateKey].schedule : state.settings.schedule
  );

  const {
    timezone,
    dateStart,
    dateEnd,
    contentText,
    isShowNothing,
    isSchedule,
    timezones,
    type,
  } = scheduleState;

  const dispatch = useDispatch();
  const { settingsFile, setSettingsFile } = useContext(ContextAll);

  const [errors, setErrors] = useState({});

  const handleDateStartChange = (e) => {
    const startDate = new Date(e);
    const endDate = new Date(dateEnd);

    if (startDate > endDate) {
      setErrors({ startDate: lang[39], endDate: lang[38] });
    } else {
      setErrors({});
    }
    dispatch(setScheduleDateStart(e));
  };

  const handleDateEndChange = (e) => {
    const startDate = new Date(dateStart);
    const endDate = new Date(e);

    if (endDate < startDate) {
      setErrors({ startDate: lang[39], endDate: lang[38] });
    } else {
      setErrors({});
    }
    dispatch(setScheduleDateEnd(e));
  };

  const today = new Date();
  const nextYear = new Date().setFullYear(today.getFullYear() + 1);

  const timezonesOptions = timezones.map((item) => ({
    value: item.id,
    label: `(GMT${item.gmt}) ${item.name}`,
  }));

  return (
    <div className={`${scheduleTitle ? "" : "mb-4"}`}>
      {!stateKey && (
        <div>
          <Form.Check
            type="checkbox"
            label={lang[22]}
            checked={isSchedule}
            onChange={() => dispatch(setIsSchedule(!isSchedule))}
            id="scheduleAccess"
            className="me-2 d-inline-block fw-bold"
          />
          <OverlayTrigger
            overlay={<Tooltip className="opacity-75">{lang[45]}</Tooltip>}
          >
            <div className="d-inline">
              <InfoIcon className="mb-1" />
            </div>
          </OverlayTrigger>
        </div>
      )}
      {(isSchedule || scheduleTitle) && (
        <div>
          <Row className="align-items-center flex-nowrap position-relative">
            <Col md={scheduleTitle ? 11 : 12}>
              <InputReactSelect
                options={timezonesOptions}
                placeholder={lang[42]}
                label={`${lang[23]}${scheduleTitle ? ` (${optional})` : ""}`}
                className="mt-3"
                value={timezonesOptions.find(
                  (option) => option.value === timezone
                )}
                onChange={(selectedOption) => {
                  dispatch(setScheduleTimezone(selectedOption.value));
                }}
              />
            </Col>
            {scheduleTitle && (
              <div className="position-absolute top-0 end-0 w-auto mt-4 pt-2 me-4 pe-4 me-md-4 pe-md-1">
                <PopoverBtn text={bookingLang[15]} />
              </div>
            )}
          </Row>

          <div className="position-relative">
            <Row
              className={`mt-2 gx-3 gx-md-0 mb-3${
                scheduleTitle ? " col-md-11" : ""
              }`}
            >
              <CustomCol>
                <DateInput
                  minDate={today}
                  maxDate={nextYear}
                  placeholder={dateLang[24]}
                  locale={locale}
                  label={lang[25]}
                  popoverText={null}
                  value={dateStart}
                  onChange={handleDateStartChange}
                  className={`w-100 ${
                    errors.startDate || (error && !dateStart.length)
                      ? "is-invalid"
                      : ""
                  }`}
                />
                {errors.startDate && (
                  <div className="text-danger">{errors.startDate}</div>
                )}
              </CustomCol>
              <Col xs={1} className="d-none d-md-block">
                <div className="d-flex justify-content-center mt-4 pt-2 h-100">
                  <DashIcon />
                </div>
              </Col>
              <CustomCol>
                <DateInput
                  minDate={today}
                  maxDate={nextYear}
                  placeholder={dateLang[24]}
                  locale={locale}
                  label={lang[26]}
                  popoverText={null}
                  value={dateEnd}
                  onChange={handleDateEndChange}
                  className={`w-100 ${
                    errors.endDate || (error && !dateEnd.length)
                      ? "is-invalid"
                      : ""
                  }`}
                />
                {errors.endDate && (
                  <div className="text-danger">{errors.endDate}</div>
                )}
              </CustomCol>
              {error && (!dateEnd.length || !dateStart.length) && (
                <div className="text-danger error-field">{errorMsg[2]}</div>
              )}
            </Row>

            {scheduleTitle && (
              <div className="position-absolute top-0 end-0 w-auto mt-4 pt-1 me-3">
                <PopoverBtn text={bookingLang[16]} />
              </div>
            )}
          </div>

          <ScheduleDays
            stateKey={stateKey}
            addDayItem={addDayItem}
            applyAll={applyAll}
            removeDayItem={removeDayItem}
            resetDayField={resetDayField}
            setDayField={setDayField}
            setToggleActiveDay={setToggleActiveDay}
            scheduleTitle={scheduleTitle}
            error={error}
          />

          {!stateKey && (
            <>
              <Row
                xs="auto"
                className="mt-4 justify-content-between align-items-center"
              >
                <Col>
                  <div className="fw-bold">{lang[27]}</div>
                </Col>
                <Col>
                  <Form.Check
                    type="checkbox"
                    label={lang[28]}
                    id="showNothing"
                    className="fs-7 custom-check custom-check-small w-100 mt-1"
                    checked={isShowNothing}
                    onChange={(e) => {
                      dispatch(setScheduleIsShowNothing(!isShowNothing));
                      !e.target.checked && dispatch(setScheduleContentText(""));
                    }}
                  />
                </Col>
              </Row>
              {!isShowNothing && (
                <>
                  {type.file && (
                    <FileUpload
                      className="mt-3"
                      files={settingsFile}
                      onupdatefiles={(files) => setSettingsFile(files)}
                      allowFileTypeValidation={true}
                      allowMultiple={false}
                      maxFiles={1}
                      name="file"
                      label={lang[29]}
                      popoverText={null}
                    />
                  )}
                  {type.link && (
                    <InputText
                      placeholder={lang[30]}
                      popoverText={null}
                      className="mb-0 mt-2 mt-md-3"
                      value={contentText}
                      onChange={(e) =>
                        dispatch(setScheduleContentText(e.target.value))
                      }
                      disabled={isShowNothing}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ScheduleAccess;

const CustomCol = styled(Col)`
  width: 45.833%;
`;
